import { useCallback, useMemo, useReducer } from "react";
import { useRouter } from "next/router";
import { useEffect } from "@remi/next-ui";
import { useMedia } from "@remi/next-ui/hooks";
import { createId } from "@remi/ts-utils";

import { useViewportBounds } from "~/components/popout/useViewportBounds";
import {
  MAX_POPOUTS,
  type PopoutContent,
  type PopoutConversation,
  PopoutManagerContext,
  type PopoutState,
  type PopoutTask,
  type PopoutTicket,
} from "~/contexts/PopoutManagerContext";
import {
  initialPopoutState,
  type PopoutAction,
  popoutManagerReducer,
  type ReducerEnv,
} from "~/contexts/popoutManagerReducer";

// Popouts persist while navigating within the same bid or project — switching
// conversations/tabs under the same context shouldn't close them.
function getPopoutContext(path: string): string {
  const cleanPath = (path.split("?")[0] ?? "").split("#")[0] ?? "";
  const segments = cleanPath.split("/").filter(Boolean);
  if (segments[0] === "dashboard" && segments[1] === "bids" && segments[2]) {
    return `/dashboard/bids/${segments[2]}`;
  }
  if (segments[0] === "dashboard" && segments[1] && /^\d+$/.test(segments[1])) {
    return `/dashboard/${segments[1]}`;
  }
  return cleanPath;
}

export const PopoutManagerProvider = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const bounds = useViewportBounds();
  const { isMobile } = useMedia();

  const env: ReducerEnv = useMemo(
    () => ({
      now: () => Date.now(),
      createId,
      viewport: { width: bounds.maxWidth },
      maxPopouts: MAX_POPOUTS,
    }),
    [bounds.maxWidth],
  );

  const reducer = useCallback(
    (state: PopoutState, action: PopoutAction): PopoutState => popoutManagerReducer(state, action, env),
    [env],
  );

  const [state, dispatch] = useReducer(reducer, initialPopoutState);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (getPopoutContext(url) !== getPopoutContext(router.asPath)) {
        dispatch({ type: "CLEAR" });
      }
    };
    router.events.on("routeChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router.events, router.asPath]);

  // On mobile a popout renders as a single bottom sheet, so opening replaces any other one.
  const openConversationPopout = useCallback(
    (conversation: PopoutConversation) => {
      dispatch({ type: "OPEN", content: { kind: "conversation", conversation }, replace: isMobile });
    },
    [isMobile],
  );

  const openTicketPopout = useCallback(
    (ticket: PopoutTicket) => {
      dispatch({ type: "OPEN", content: { kind: "ticket", ticket }, replace: isMobile });
    },
    [isMobile],
  );

  const openTaskPopout = useCallback(
    (task: PopoutTask) => {
      dispatch({ type: "OPEN", content: { kind: "task", task }, replace: isMobile });
    },
    [isMobile],
  );

  const openPopouts = useCallback((contents: PopoutContent[]) => {
    dispatch({ type: "OPEN_MANY", contents });
  }, []);

  const closePopout = useCallback((popoutId: string) => {
    dispatch({ type: "CLOSE", popoutId });
  }, []);

  const minimizePopout = useCallback((popoutId: string) => {
    dispatch({ type: "MINIMIZE", popoutId });
  }, []);

  const minimizeAllPopouts = useCallback(() => {
    dispatch({ type: "MINIMIZE_ALL" });
  }, []);

  const restorePopout = useCallback((popoutId: string) => {
    dispatch({ type: "RESTORE", popoutId });
  }, []);

  const raisePopout = useCallback((popoutId: string) => {
    dispatch({ type: "RAISE", popoutId });
  }, []);

  const value = useMemo(
    () => ({
      state,
      openConversationPopout,
      openTicketPopout,
      openTaskPopout,
      openPopouts,
      closePopout,
      minimizePopout,
      minimizeAllPopouts,
      restorePopout,
      raisePopout,
    }),
    [
      state,
      openConversationPopout,
      openTicketPopout,
      openTaskPopout,
      openPopouts,
      closePopout,
      minimizePopout,
      minimizeAllPopouts,
      restorePopout,
      raisePopout,
    ],
  );

  return <PopoutManagerContext.Provider value={value}>{children}</PopoutManagerContext.Provider>;
};
